import React from "react";
import { Row, Col, message, Form, InputNumber } from 'antd';
import sum from 'lodash/sum';
import { getLangList } from "@/lib/i18n";
import { ReButton, ReModal } from "@/components/refineElement";
import { showDelConfirm, getOnOkFunction } from "@/lib/antdElementFunction";
import SaleConfig from "@/config/sale";
import ResponsiveTable from '@/components/responsiveTable';
import { getCurrentTime, dateFormat } from "@/lib/dateTime";
import { isPositiveCategory } from "@/lib/sale";
import { hasPermission } from "@/lib/permissionUtil";
import ModalProduct from './ModalProduct';
import ModalCheckout from './ModalCheckout';
import { SaleSubjectType } from './types';

const { createSaleProductInfo, updateSaleProductInfo, deleteSaleProductInfo, getSaleProductInfo, purchase } = SaleConfig;

interface props {
  selectedGroupName: string,
  selectedSaleSubjectId: string,
  saleSubjectArray: SaleSubjectType[],
}

interface state {
  isModalVisible: boolean,
  isModalCheckoutVisible: boolean,
  handleType: string,
  productInfo: any,
  productArray: Array<object>,
  purchaseQuantityObject: any,
  purchasePriceTotalObject: any,
  purchaseDiscountObject: any
}

class Product extends React.Component<props, state> {
  state = {
    isModalVisible: false,
    isModalCheckoutVisible: false,
    handleType: '',
    productInfo: {} as never as any,
    productArray: [],
    purchaseQuantityObject: {} as never as any,
    purchasePriceTotalObject: {} as never as any,
    purchaseDiscountObject: {} as never as any,
  };
  saleT: any = getLangList("sale");
  errorT: any = getLangList("error");
  buttonT: any = getLangList("button");
  formRef: any = React.createRef();

  componentDidMount() {
    this.getSaleProductInfo();
  }

  columns = [
    { title: this.saleT.ITEM, dataIndex: "itemName" },
    { title: this.saleT.UNIT_PRICE, dataIndex: "itemPrice" },
    {
      title: this.saleT.QUANTITY,
      dataIndex: "quantity",
      render: (text: any, record: any, index: any) => {
        const { purchaseQuantityObject } = this.state;
        const { itemId } = record;
        const quantity = purchaseQuantityObject[itemId] ?? 0;

        return (
          <Form.Item name={`quantity_${index}`} initialValue={0}>
            <InputNumber
              min={0}
              onChange={(value) => this.changeQuantity(value, record, index)}
              controls={false}
              addonBefore={<div onClick={() => this.changeQuantity(quantity - 1, record, index, 'click')}>-</div>}
              addonAfter={<div onClick={() => this.changeQuantity(quantity + 1, record, index, 'click')}>+</div>}
            />
          </Form.Item>
        );
      },
    },
    {
      title: this.saleT.AMOUNT,
      dataIndex: "amount",
      render: (text: any, record: any, index: any) => {
        return (
          <Form.Item name={`amount_${index}`} initialValue={`$0`}>
            <InputNumber bordered={false} readOnly className="sale-disabled-input" />
          </Form.Item>
        );
      }
    },
    {
      title: this.saleT.OPERATE,
      dataIndex: "action",
      render: (text: any, record: any) => {
        const { itemId } = record;
        return (
          <div className="action-bar">
            {hasPermission('SALE_EDIT') &&
              <div>
                <ReButton
                  iconName='edit'
                  onClick={this.clickEdit(record)}
                >
                  {this.buttonT.EDIT}
                </ReButton>
              </div>
            }
            {hasPermission('SALE_DELETE') &&
              <div>
                <ReButton
                  iconName='delete'
                  onClick={this.clickDelete(itemId)}
                  attributeObject={{ type: 'danger' }}
                >
                  {this.buttonT.DELETE}
                </ReButton>
              </div>
            }
          </div>
        );
      }
    },
  ];

  getSaleProductInfo = () => {
    const { selectedSaleSubjectId } = this.props;
    const params = { saleSubjectId: selectedSaleSubjectId };

    getSaleProductInfo(params).then((res: any) => {
      const productArray = res?.value ?? [];
      this.setState({ productArray }, this.resetQuantityAmount);
    });
  }

  resetQuantityAmount = () => {
    const { productArray } = this.state;
    productArray.forEach((item: any, index: number) => {
      this.formRef.current.setFieldsValue({
        [`quantity_${index}`]: `0`,
        [`amount_${index}`]: `$0`,
      });
    });
  }

  resetPurchaseObject = () => this.setState({
    purchaseQuantityObject: {},
    purchasePriceTotalObject: {},
    purchaseDiscountObject: {},
  });

  changeQuantity = (value: any, record: any, index: any, action: string = '') => {
    const { purchaseQuantityObject, purchasePriceTotalObject, purchaseDiscountObject } = this.state;
    const { itemPrice, itemId, discount = 0 } = record;

    this.formRef.current.setFieldsValue({ [`amount_${index}`]: `$${(value * itemPrice)}` });
    action === 'click' && this.formRef.current.setFieldsValue({ [`quantity_${index}`]: value });

    this.setState({
      purchaseQuantityObject: { ...purchaseQuantityObject, [itemId]: value },
      purchasePriceTotalObject: { ...purchasePriceTotalObject, [itemId]: value * itemPrice },
      purchaseDiscountObject: { ...purchaseDiscountObject, [itemId]: discount },
    });
  }

  toggleModal = (handleType: string = '') => {
    const { isModalVisible } = this.state;
    this.setState({ isModalVisible: !isModalVisible, handleType });
  }

  toggleCheckoutModal = () => {
    const { isModalCheckoutVisible } = this.state;
    this.setState({ isModalCheckoutVisible: !isModalCheckoutVisible });
  }

  clickCreate = () => {
    this.setState({ productInfo: {} }, () => this.toggleModal('create'));
  }

  clickEdit = (data: object) => () => {
    this.setState({ productInfo: data }, () => this.toggleModal('update'));
  }

  clickDelete = (id: string) => () => {
    showDelConfirm(() => this.deleteSaleProductInfo(id));
  };

  clickCheckout = () => {
    this.toggleCheckoutModal();
  }

  createSaleProductInfo = (values: any) => {
    const { itemName, itemPrice = 0, discount, saleSubjectId } = values;
    const params = {
      itemName,
      itemPrice,
      discount,
      itemQuantity: 0,
      saleSubjectId,
    };

    createSaleProductInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(this.errorT.failed);
      }
      message.success(this.errorT.success);
      this.toggleModal();
      this.getSaleProductInfo();
    });
  }

  updateSaleProductInfo = (values: any) => {
    const { productInfo } = this.state;
    const { itemId } = productInfo;
    const { itemName, itemPrice, discount = 0, saleSubjectId } = values;
    const params = {
      itemName,
      itemPrice,
      discount,
      itemQuantity: 0,
      itemId,
      saleSubjectId,
    };

    updateSaleProductInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(this.errorT.failed);
      }
      message.success(this.errorT.success);
      this.toggleModal();
      this.getSaleProductInfo();
    });
  }

  deleteSaleProductInfo = (id: string) => {
    const params = { itemId: id };

    deleteSaleProductInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(this.errorT.failed);
      }
      message.success(this.errorT.success);
      this.getSaleProductInfo();
    });
  }

  purchase = (values: any) => {
    const { selectedGroupName } = this.props;
    const { userAccountId, isDiscount} = values;
    const purchaseList = this.getPurchaseList(isDiscount);
    const userOriginalPurchaseTotalAmount = this.getOriginalPurchaseAmount();
    const purchaseDiscountAmount = this.getPurchaseDiscountAmount();
    const userPurchaseTotalAmount = isDiscount ? userOriginalPurchaseTotalAmount - purchaseDiscountAmount : userOriginalPurchaseTotalAmount;
    const params = {
      userAccountId,
      purchaseList,
      userPurchaseTotalAmount: isPositiveCategory(selectedGroupName) ? userPurchaseTotalAmount : userPurchaseTotalAmount * -1,
    };

    purchase(params).then((res: any) => {
      if (!res.success) {
        return message.error(this.errorT.failed);
      }
      message.success(this.errorT.success);
      this.resetQuantityAmount();
      this.resetPurchaseObject();
      this.toggleCheckoutModal();
    });
  }

  getPurchaseList = (isDiscount: boolean) => {
    const { selectedGroupName } = this.props;
    const { purchaseQuantityObject, purchasePriceTotalObject, purchaseDiscountObject } = this.state;
    const purchaseList: any = [];

    Object.entries(purchaseQuantityObject).forEach(([key, value]: any) => {
      const purchaseDate = getCurrentTime().format(dateFormat);
      const discount = isDiscount ? purchaseDiscountObject[key] : 0; // 學生沒有折扣
      const singleItemTotal = purchasePriceTotalObject[key] - (discount * value);
      const singleItemPrice = purchasePriceTotalObject[key] / value;

      value > 0 && purchaseList.push({
        purchaseItemId: key,
        purchaseTotalAmount: isPositiveCategory(selectedGroupName) ? singleItemTotal : -singleItemTotal,
        purchaseSinglePrice: singleItemPrice,
        purchaseQuantity: value,
        purchaseDate,
        discount,
      });
    });
    return purchaseList;
  }

  getOriginalPurchaseAmount = () => {
    const { purchasePriceTotalObject } = this.state;
    const purchaseAmount = sum(Object.entries(purchasePriceTotalObject).map(([key, value]: any) => value));
    return purchaseAmount ?? 0;
  }

  getPurchaseDiscountAmount = () => {
    const { purchaseQuantityObject, purchaseDiscountObject } = this.state;
    const purchaseDiscountAmount = sum(Object.entries(purchaseDiscountObject).map(([key, value]: any) => value * purchaseQuantityObject[key]));
    return purchaseDiscountAmount ?? 0;
  }

  getTotalDiscount = () => {
    const { purchaseDiscountObject, purchaseQuantityObject } = this.state;
    const totalDiscount = sum(
      Object.entries(purchaseDiscountObject).map(([key, value]: any) => value * purchaseQuantityObject[key])
    );
    return totalDiscount ?? 0;
  }

  isCartEmpty = () => {
    const { purchaseQuantityObject } = this.state;

    for (const item of Object.entries(purchaseQuantityObject)) {
      const [, value]: any = item;
      if (value > 0) {
        return false;
      }
    }
    return true;
  }

  render() {
    const { selectedGroupName, saleSubjectArray, selectedSaleSubjectId } = this.props;
    const { isModalVisible, isModalCheckoutVisible, handleType, productInfo, productArray } = this.state;
    const okFunction = {
      create: this.createSaleProductInfo,
      update: this.updateSaleProductInfo,
    };

    return (
      <>
        <Form
          ref={this.formRef}
          className="sale-form"
        >
          <Row gutter={[10, 20]}>
            <Col span={24}>
              <div className="re-button-container-right porduct-search-bar">
                {hasPermission('SALE_NEW') &&
                  <Form.Item>
                    <ReButton
                      iconName='plus'
                      attributeObject={{ disabled: selectedGroupName === '' }}
                      onClick={this.clickCreate}
                    >
                      {this.saleT.NEW_PRODUCT}
                    </ReButton>
                  </Form.Item>
                }

                <Form.Item>
                  <ReButton
                    attributeObject={{ disabled: this.isCartEmpty() }}
                    onClick={this.clickCheckout}
                  >
                    {this.saleT.CHECKOUT}
                  </ReButton>
                </Form.Item>
              </div>
            </Col>

            <Col span={24}>
              <ResponsiveTable
                dataSource={productArray}
                columns={this.columns}
              />
            </Col>
          </Row>
        </Form>

        {isModalVisible &&
          <ReModal
            title={this.saleT.PRODUCT}
            onCancel={() => this.toggleModal()}
            visible={isModalVisible}
            onOk={(data: any) => getOnOkFunction(handleType, data, okFunction)}
            CollectedForm={ModalProduct}
            formProps={{
              productInfo,
              handleType,
              saleSubjectArray,
              selectedSaleSubjectId,
            }}
          />
        }

        {isModalCheckoutVisible &&
          <ReModal
            title={this.saleT.CHECKOUT}
            onCancel={() => this.toggleCheckoutModal()}
            visible={isModalCheckoutVisible}
            onOk={(data: any) => this.purchase(data)}
            CollectedForm={ModalCheckout}
            formProps={{
              purchaseAmount: this.getOriginalPurchaseAmount(),
              purchaseDiscountAmount: this.getPurchaseDiscountAmount(),
              selectedGroupName,
            }}
          />
        }
      </>
    )
  }
}

export default Product;